import { useNotification } from "@context/NotificationContext";
import { STATUS_KEYS } from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import type { UpdatePortServiceTimesPayload } from "@services/port-services/types";
import { updatePortServiceTimes } from "@services/portServicesApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { t } from "i18next";

interface UseUpdatePortServiceProps {
  status: string;
  portServiceId: string;
  ets: string | Date | null;
  etc: string | Date | null;
  setShouldRefetch: (value: boolean) => void;
  onClose: () => void;
}

const LANG_BASE = "vessel-call.port-services";

export interface UseUpdatePortServiceResult {
  onSubmit: () => Promise<void>;
}

export const useUpdatePortService = (
  props: UseUpdatePortServiceProps,
): UseUpdatePortServiceResult => {
  const { portServiceId, ets, etc, status, setShouldRefetch, onClose } = props;
  const { notify } = useNotification();

  const updateService = useMutation<unknown, unknown, UpdatePortServiceTimesPayload>({
    mutationFn: (payload) => {
      return runTransaction(
        {
          module: "vessel_call",
          screen: "PortServiceDetails",
          action: "update_port_service_times",
          entityType: "port_service",
        },
        (transactionId) =>
          updatePortServiceTimes(
            "bff-vessel-call",
            portServiceId,
            payload,
            ...(transactionId ? [transactionId] : []),
          ),
      );
    },
    onSuccess: () => {
      setShouldRefetch?.(true);
      onClose();
      notify({
        title: t(`${LANG_BASE}.notifications.service-update-success-title`),
        description: t(`${LANG_BASE}.notifications.service-update-success-description`),
        type: "success",
      });
    },
    onError: () => {
      if ((status === STATUS_KEYS.STARTED && !etc) || (!ets && !etc)) {
        notify({
          title: t(`${LANG_BASE}.notifications.service-update-error-title`),
          description: t(`${LANG_BASE}.notifications.service-update-error-no-times-description`),
          type: "error",
        });
        return;
      }

      notify({
        title: t(`${LANG_BASE}.notifications.service-update-error-title`),
        description: t(`${LANG_BASE}.notifications.service-update-error-description`),
        type: "error",
      });
    },
  });

  const hasValue = (value: string | Date | null | undefined): boolean =>
    value !== null && value !== undefined && value !== "";

  const onSubmit = async (): Promise<void> => {
    const payload: UpdatePortServiceTimesPayload = {};

    if (status === STATUS_KEYS.STARTED) {
      if (hasValue(etc)) {
        payload.etc = etc;
      }
    } else {
      if (hasValue(etc)) {
        payload.etc = etc;
      }

      if (hasValue(ets)) {
        payload.ets = ets;
      }
    }

    updateService.mutateAsync(payload);
  };

  return { onSubmit };
};
